import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';

/** Below this the card already matches — skip the resize and its undo entry. */
const DURATION_TOLERANCE_SECONDS = 0.05;

/** Narrow port over the editor: just the lookup and the rippling resize. */
export interface CardResizeEditor {
  getElementById(id: string): { durationSeconds: number } | undefined;
  commandResizeElementRippleAllTracks(
    elementId: string,
    newDuration: number,
  ): boolean;
}

export interface MatchCardToVideoDurationDeps {
  editor: CardResizeEditor;
  /** Reads a media file's length in seconds from its metadata. */
  getMediaDuration: (url: string) => Promise<number>;
}

/**
 * PLAT-11732 — fits a blank media card to the video placed in it.
 *
 * A card template that opts in (`matchCardDuration` on its video control) has
 * no length of its own: once a video is chosen, the card takes the video's
 * length and everything after it shifts by the difference. Reading the length
 * from the file's metadata, rather than waiting on Dato's processed duration,
 * lets a just-uploaded video fit the card immediately.
 */
export class MatchCardToVideoDurationService {
  private readonly _editor: CardResizeEditor;
  private readonly _getMediaDuration: (url: string) => Promise<number>;
  private readonly _logger = LogManager.getLogger(
    'service:MatchCardToVideoDurationService',
  );
  /** The last video picked per card, so an older pick's late length is dropped. */
  private readonly _latestVideoByCard = new Map<string, string>();

  constructor(deps: MatchCardToVideoDurationDeps) {
    this._editor = deps.editor;
    this._getMediaDuration = deps.getMediaDuration;
  }

  /** Resizes the card to the video's length; returns the length applied. */
  async matchCardToVideo(
    cardElementId: string,
    videoUrl: string,
  ): Promise<Result<number>> {
    this._latestVideoByCard.set(cardElementId, videoUrl);
    let duration: number;
    try {
      duration = await this._getMediaDuration(videoUrl);
    } catch (error) {
      this._logger.warn('Could not read video duration', {
        videoUrl,
        ...extractErrorInfo(error),
      });
      return err('EXTERNAL', 'Could not read video duration', error);
    }

    if (this._latestVideoByCard.get(cardElementId) !== videoUrl)
      return err('PRECONDITION', 'A newer video was picked for this card');

    if (!Number.isFinite(duration) || duration <= 0) {
      this._logger.warn('Video reported no usable duration', {
        videoUrl,
        duration,
      });
      return err('VALIDATION', 'Video reported no usable duration');
    }

    // The editor may have moved on while the metadata loaded.
    const card = this._editor.getElementById(cardElementId);
    if (!card) return err('NOT_FOUND', 'Card element not found');

    if (Math.abs(card.durationSeconds - duration) < DURATION_TOLERANCE_SECONDS)
      return ok(card.durationSeconds);

    if (
      !this._editor.commandResizeElementRippleAllTracks(cardElementId, duration)
    ) {
      return err('UNKNOWN', 'Card resize failed');
    }
    return ok(duration);
  }
}
